<script setup>
import * as yup from "yup";
import {useDjangoForm} from "@/utils/forms";
import {numberValidator, requiredMessage} from "@/utils/yupValidators";
import {HpoRoleLabel} from "@/components/hpo/choices";
import {CreateDeviceMutation} from "@/graphql/mutations/device/createDevice.graphql";
import {useMutation} from "@vue/apollo-composable";

const memberTypeOptions = Object.entries(HpoRoleLabel).map(([value, title]) => ({value: value.toString(), title}));

const model = defineModel({type: Boolean});
const emit = defineEmits(['createDevice']);

const {mutate} = useMutation(CreateDeviceMutation);

const schema = yup.object({
    'name': yup.string().required(requiredMessage),
    'defaultMemberType': numberValidator,
});
const {defineField, handleMutationSubmit, isSubmitting, resetForm} = useDjangoForm({
    validationSchema: schema,
    initialValues: {
        name: '',
        defaultMemberType: null,
    },
});

const [name, nameProps] = defineField('name');
const [defaultMemberType, defaultMemberTypeProps] = defineField('defaultMemberType');

const onSubmit = handleMutationSubmit(mutate, {
    onSuccess(values) {
        emit('createDevice', {
            id: values.device.id,
            name: values.device.name,
            code: values.deviceAuthenticationRequest.code,
            codeExpiresAt: values.deviceAuthenticationRequest.expiresAt,
            authenticationUrl: values.deviceAuthenticationRequest.authenticationUrl,
        });
        close();
    }
});

function close() {
    resetForm();
    model.value = false;
}
</script>

<template>
    <v-dialog v-model="model" max-width="640">
        <form @submit.prevent="onSubmit">
            <v-card>
                <v-card-title>
                    {{ pgettext('title', 'New Device') }}
                </v-card-title>

                <v-divider />

                <v-card-text>
                    <v-text-field v-model="name"
                                  v-bind="nameProps"
                                  :label="pgettext('label', 'Name')"/>
                    <v-select v-model="defaultMemberType"
                              v-bind="defaultMemberTypeProps"
                              :items="memberTypeOptions"
                              :label="pgettext('label', 'Default Member Type')"
                              clearable/>
                </v-card-text>

                <v-card-actions class="bg-surface-light">
                    <v-btn type="button" @click="close()">
                        {{ pgettext('action', 'Cancel') }}
                    </v-btn>

                    <v-spacer></v-spacer>

                    <v-btn type="submit" color="green" variant="flat" :loading="isSubmitting">
                        {{ pgettext('action', 'Create Device') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </form>
    </v-dialog>
</template>
